import React from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { flowRight } from 'lodash'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { withErrorBoundary } from 'lib/errorBoundary'
import { getVehiclePriceEstimateModalUrl, getVehicleEventsPath } from 'lib/utils/store/vehicle'
import { getLocale } from 'lib/utils/getLocale'

import Link from 'components/Link'
import PriceCloud from 'components/Vehicles/VehiclePriceEstimate/PriceCloud/PriceCloud'
import Button from 'components/UI/Button'
import VehiclePriceEstimateLogic from 'components/Vehicles/VehiclePriceEstimate/VehiclePriceEstimateLogic'
import ErrorComponent from 'components/Errors/ErrorComponent'
import LoadingIndicator from 'components/LoadingIndicator'

import './vehicle-price-estimate-display.pcss'

const VehiclePriceEstimateDisplay = ({
    vehicle,
    notResponsive,
    displayAutoVista,
    priceCloudWithUpdate,
    onClose,
    onSuccess,
    displayAddPriceEstimateOnMount,
    displayCreateEventButton,
    eventCreated,
}) => {
    const {
        id,
        estimate,
        estimatedAt,
        mileage,
        sellDuration
    } = vehicle
    const { t, i18n } = useTranslation('vehicles')
    const location = useLocation()

    const handleSubmitPricePredictionEvent = async (createVehiclePricePredictionEvent) => {
        await createVehiclePricePredictionEvent(vehicle.id).then(() => {
            onSuccess()
        })
    }

    return (
        <div
            className={classNames('vehicle-price-estimate-display', {
                'vehicle-price-estimate-display--not-responsive': notResponsive,
            })}
        >
            {estimatedAt ? (
                <>
                    <div className="vehicle-price-estimate-display__sections">
                        <div className="vehicle-price-estimate-display__section vehicle-price-estimate-display__section--large">
                            <div className="vehicle-price-estimate-display__section-title">
                                {t('vehicles:pricePrediction.priceEstimate')}
                            </div>
                            <div className="vehicle-price-estimate-display__section-sub-title">
                            </div>
                            <div className="vehicle-price-estimate-display__section-value">
                                {estimate} €
                            </div>
                        </div>
                        <div className="vehicle-price-estimate-display__section vehicle-price-estimate-display__section--large">
                            <div className="vehicle-price-estimate-display__section-title">
                                {t('vehicles:pricePrediction.salesTime')}
                            </div>
                            <div className="vehicle-price-estimate-display__section-sub-title">
                            </div>
                            <div className="vehicle-price-estimate-display__section-value">
                                {sellDuration ? Math.round(sellDuration) : '-'} pv
                            </div>
                        </div>
                        <div className="vehicle-price-estimate-display__section vehicle-price-estimate-display__mileage">
                            <div className="vehicle-price-estimate-display__section-title">
                                {t('vehicleProfile:mileage')}
                            </div>
                            <div className="vehicle-price-estimate-display__section-sub-title">
                            </div>
                            <div className="vehicle-price-estimate-display__section-value">
                                <span>{mileage} km</span>
                            </div>
                        </div>
                    </div>
                    {(displayAddPriceEstimateOnMount && displayCreateEventButton) ? (
                        <VehiclePriceEstimateLogic>
                            {({ loading, error, createVehiclePricePredictionEvent }) => {
                                if (error) return <ErrorComponent error={error} />
                                if (loading) return <LoadingIndicator />

                                return (
                                    <div className="vehicle-price-estimate-display__add-price-estimate-event">
                                        <Button
                                            trackName="addPriceEstimateToTimeline"
                                            onClick={() => handleSubmitPricePredictionEvent(createVehiclePricePredictionEvent)}
                                            className="button button--secondary"
                                            type="button"
                                        >
                                            {t('vehicles:pricePrediction.addPriceEstimateToTimeline.title')}
                                        </Button>
                                        <span>{t('vehicles:pricePrediction.addPriceEstimateToTimeline.description')}</span>
                                    </div>
                                )
                            }}
                        </VehiclePriceEstimateLogic>
                    ) : (
                        eventCreated && (
                            <div className="vehicle-price-estimate-display__timeline-button">
                                <Link
                                    className="button button--secondary"
                                    to={`${getLocale(i18n.language)}${getVehicleEventsPath(vehicle.id)}`}
                                    trackName="gotoTimeline"
                                >
                                    {t('common:gotoVehicleTimeline')}
                                </Link>
                                <span>{t('vehicles:pricePrediction.priceEstimateAddedToTimeline')}</span>
                            </div>
                        )
                    )}
                    {(vehicle) && (
                        <PriceCloud
                            vehicle={vehicle}
                            notResponsive={notResponsive}
                            withUpdateLink={priceCloudWithUpdate}
                            onClose={onClose}
                        />
                    )}
                </>
            ) : (
                <div className="vehicle-price-estimate-display__no-estimate">
                    <Link
                        to={getVehiclePriceEstimateModalUrl(location, id)}
                        trackName="showPricePredictionForm"
                        className="button button--centered detail-section__cta"
                        type="button"
                    >
                        {t('vehicles:pricePrediction.cta.showPriceEstimate')}
                    </Link>
                    <span>{t('vehicles:pricePrediction.infoText.priceEstimate')}</span>
                </div>
            )}
        </div>
    )
}

VehiclePriceEstimateDisplay.propTypes = {
    displayAddPriceEstimateOnMount: PropTypes.bool,
    displayCreateEventButton: PropTypes.bool,
    eventCreated: PropTypes.bool,
    notResponsive: PropTypes.bool,
    onSuccess: PropTypes.func,
    vehicle: PropTypes.object,
}

export default flowRight(
    withErrorBoundary,
)(VehiclePriceEstimateDisplay)
